import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container, Typography, Box, IconButton, Button, Chip,
  Card, CardContent, Avatar, Tooltip, alpha, CircularProgress, useTheme,
  Menu, MenuItem, ListItemIcon, ListItemText,
} from '@mui/material';
import {
  Edit as EditIcon, Archive as ArchiveIcon, Restore as RestoreIcon, Add as AddIcon,
  Business as BusinessIcon, AttachMoney as MoneyIcon,
  Assignment as AssignmentIcon, People as PeopleIcon,
  Star as StarIcon, CheckCircle as ActiveIcon,
  Visibility as VisibilityIcon, Assessment as ReportIcon,
  PictureAsPdf as PdfIcon, TableChart as CsvIcon,
  Handshake as HandshakeIcon,
} from '@mui/icons-material';
import FilterBar from '../../common/FilterBar';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import StatCards from '../../common/StatCards';
import TableList from '../../common/TableList';
import ListBulkToolbar from '../../common/ListBulkToolbar';
import { PARTNER_BULK_CONFIG } from '../../../utils/bulkUpdateConfigs';
import { useListBulkSelection, bulkTableProps, createBulkHandlers } from '../../../utils/listBulkSelection';

import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import { SUCCESS, glassCardSx } from '../../common/SaaSTokens';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { canAccessPartnershipManagement } from '../../../utils/partnershipPermissions';
import { motion } from 'framer-motion';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';

// ─── Design tokens — cyberpunk dark system ────────────────────────────────────
const B = { 600: '#2563EB', 700: '#1D4ED8', 900: '#1E3A8A' };
const NEON  = '#38BDF8';
const NEON2 = '#818CF8';
const NEON3 = '#34D399';
const SURFACE_BASE = '#050d1a';
const SURFACE_MID  = '#0a1628';

const DOT_GRID   = `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='10' cy='10' r='0.55' fill='%2338BDF8' fill-opacity='0.07'/%3E%3C/svg%3E")`;

const PL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=Rajdhani:wght@500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
  @keyframes bar-sweep { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
`;

const MotionCard = motion(Card);

const cardShellSx = {
  ...glassCardSx,
  overflow: 'hidden',
  position: 'relative',
};

// Colour helpers
const getStatusNeon = (s) => ({ active: NEON3, inactive: '#F87171', pending: '#FCD34D', archived: '#94A3B8' }[s] || alpha(NEON, 0.5));
const getStatusLabel = (status) => {
  return status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown';
};
const partnerDisplayStatus = (partner) => partner?.archived_at ? 'archived' : partner?.status;
const tierSlug = (p) => {
  const name = (p?.sponsor_tier?.tier_name || '').toLowerCase();
  return ['diamond', 'platinum', 'gold', 'silver', 'bronze'].find(k => name.includes(k)) || '';
};
const getLevelNeon = (slug) => ({ diamond: NEON2, platinum: NEON, gold: '#FCD34D', silver: '#94A3B8', bronze: '#F97316' }[slug] || alpha(NEON, 0.5));
const getLevelEmoji = (slug) => ({ diamond: '💎', platinum: '⭐', gold: '🥇', silver: '🥈', bronze: '🥉' }[slug] || '🏷️');
const isPremiumSlug = (slug) => ['gold', 'platinum', 'diamond'].includes(slug);
const TIER_SORT_ORDER = { diamond: 0, platinum: 1, gold: 2, silver: 3, bronze: 4 };
const STATUS_SORT_ORDER = { active: 0, pending: 1, inactive: 2 };
const CURRENT_YEAR = dayjs().year();

const partnerInCurrentYear = (partner) => {
  if (!partner?.sponsor_start_date && !partner?.sponsor_end_date) return false;
  const start = partner.sponsor_start_date ? dayjs(partner.sponsor_start_date) : null;
  const end = partner.sponsor_end_date ? dayjs(partner.sponsor_end_date) : null;
  const startYear = (start || end).year();
  const endYear = (end || start).year();
  return startYear <= CURRENT_YEAR && endYear >= CURRENT_YEAR;
};

const getPartnerRepresentativeId = (partner) =>
  partner?.company?.representative_user_id
  ?? partner?.company?.representative?.id
  ?? null;

/** Map filter bar / stat-card state → highlighted stat card index. */
const activeStatFilterFromFilters = (f = {}) => {
  if (f.premium_tier === '1' && f.current_year === '1') return 'premium';
  if (f.status === 'active') return 'active';
  if (f.current_year === '1') return 'year_all';
  return 'all';
};

const STAT_FILTER_TO_INDEX = { all: 0, active: 1, year_all: 2, premium: 3 };

const PartnerList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user: currentUser } = useAuth();
const { selectedIds, setSelectedIds, clearSelection } = useListBulkSelection();
  
  const hasPartnershipAccess = canAccessPartnershipManagement(currentUser);

  const [partners, setPartners] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportMenuAnchor, setReportMenuAnchor] = useState(null);
  const [userOptions, setUserOptions] = useState([]);

  const canManagePartnerships = hasPartnershipAccess;

  const filterOptions = [
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      searchable: true,
      options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'pending', label: 'Pending' },
        { value: 'archived', label: 'Archived' },
      ],
    },
    {
      name: 'company_id',
      label: 'Company',
      type: 'select',
      searchable: true,
      options: companies.map((company) => ({
        value: String(company.id),
        label: company.name || `#${company.id}`,
      })),
    },
    {
      name: 'representative_user_id',
      label: 'Assigned to',
      type: 'select',
      searchable: true,
      options: userOptions.map((user) => ({
        value: String(user.id),
        label: user.name || user.email || `#${user.id}`,
      })),
    },
    { name: 'sponsor_start_date', label: 'Start date', type: 'date' },
    { name: 'sponsor_end_date', label: 'End date', type: 'date' },
  ];

  useEffect(() => {
    const cssId = 'partnerlist-ds-css';
    if (!document.getElementById(cssId)) { const el = document.createElement('style'); el.id = cssId; el.innerHTML = PL_CSS; document.head.appendChild(el); }
  }, []);

  useEffect(() => {
    if (!currentUser) { navigate('/login'); return; }
    if (!hasPartnershipAccess) { navigate('/dashboard'); return; }
    fetchPartners({ forceRefresh: Boolean(location.state?.refreshList) });
    fetchCompanies();
    loadFilterOptions();
    if (location.state?.partnerStatusFilter) {
      setFilters({ status: location.state.partnerStatusFilter });
      setSearchTerm('');
      setPage(0);
    }
  }, [hasPartnershipAccess, currentUser, location.pathname, location.state?.refreshList, location.state?.partnerStatusFilter]);

  const loadFilterOptions = async () => {
    try {
      const usersRes = await api.get('/users?active_only=1', { cache: false });
      setUserOptions(Array.isArray(usersRes.data) ? usersRes.data : []);
    } catch {
      setUserOptions([]);
    }
  };

  const fetchPartners = async ({ forceRefresh = false, archived = false } = {}) => {
    try {
      setLoading(true);
      const r = await api.get('/partners', {
        ...(forceRefresh ? { cache: false } : {}),
        ...(archived ? { params: { archived: 1 } } : {}),
      });
      setPartners(r.data); setError(null);
    } catch { setError('Failed to load partners. Please try again.'); }
    finally { setLoading(false); }
  };

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(Array.isArray(response.data) ? response.data : []);
    } catch {
      setCompanies([]);
    }
  };

  const handleDelete = (partner) => { setSelectedPartner(partner); setDeleteDialogOpen(true); };
  const confirmDelete = async () => {
    const partnerName = selectedPartner?.company?.name || 'Partner';
    try {
      setDeleteLoading(true);
      await api.post(`/partners/${selectedPartner.id}/archive`);
      setPartners((prev) => prev.filter((partner) => partner.id !== selectedPartner.id));
      setSearchTerm('');
      setPage(0);
      setDeleteDialogOpen(false);
      setSelectedPartner(null);
      setSuccess(`Partner "${partnerName}" has been archived successfully!`);
      await fetchPartners({ forceRefresh: true });
    } catch { setError('Failed to archive partner. Please try again.'); }
    finally { setDeleteLoading(false); }
  };

  const handleRestore = async (partner) => {
    try {
      await api.post(`/partners/${partner.id}/restore`);
      setPartners((prev) => prev.filter((item) => item.id !== partner.id));
      setSuccess(`Partner "${partner.company?.name || 'Partner'}" has been restored successfully!`);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to restore partner. Please try again.');
    }
  };

  const handleBulkArchive = async () => {
    const ids = [...(selectedIds || [])];
    if (!ids.length) {
      setError('No selected partners to archive.');
      return;
    }

    try {
      await Promise.all(ids.map((partnerId) => api.post(`/partners/${Number(partnerId)}/archive`)));
      const idSet = new Set(ids.map(String));
      setPartners((prev) => prev.filter((partner) => !idSet.has(String(partner.id))));
      setSearchTerm('');
      setPage(0);
      clearSelection();
      setError(null);
      setSuccess(`${ids.length} partner(s) archived successfully.`);
      await fetchPartners({ forceRefresh: true });
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to archive selected partners. Please try again.');
      throw err;
    }
  };

  const isReportMenuOpen = Boolean(reportMenuAnchor);

  const closeReportMenu = () => setReportMenuAnchor(null);

  const openReportMenu = (event) => {
    setReportMenuAnchor(event.currentTarget);
  };

  const buildReportRows = () => (
    sortedPartners.map((partner, index) => ({
      no: index + 1,
      company: partner.company?.name || '-',
      tier: partner.sponsor_tier?.tier_name || '-',
      amount: Number(partner.sponsor_amount || 0),
      status: partner.status || '-',
      publicity: partner.publicity || '-',
      startDate: partner.sponsor_start_date || '-',
      endDate: partner.sponsor_end_date || '-',
      notes: partner.notes || '-',
    }))
  );

  const generatePartnerCsvReport = () => {
    const rows = buildReportRows();
    const generatedAt = dayjs().format('YYYY-MM-DD HH:mm');
    const quoteCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const headers = ['No', 'Company', 'Tier', 'Sponsor Amount (RM)', 'Status', 'Publicity', 'Start Date', 'End Date', 'Notes'];
    const filtersSummary = `Status: ${filters.status || 'All'} | Search: ${searchTerm || 'None'}`;

    const summaryRows = [
      ['PARTNER REPORT'],
      ['Generated At', generatedAt],
      ['Generated By', currentUser?.name || 'System'],
      ['Applied Filters', filtersSummary],
      ['Total Records', rows.length],
      [''],
    ];

    const csvRows = rows.map((row) => [
      row.no,
      quoteCsv(row.company),
      quoteCsv(row.tier),
      row.amount.toFixed(2),
      quoteCsv(row.status),
      quoteCsv(row.publicity),
      quoteCsv(row.startDate),
      quoteCsv(row.endDate),
      quoteCsv(row.notes),
    ]);

    const csvContent = [...summaryRows, headers, ...csvRows]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `Partner_Report_${dayjs().format('YYYYMMDD_HHmmss')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const generatePartnerPdfReport = () => {
    const rows = buildReportRows();
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const marginX = 36;
    const pageWidth = doc.internal.pageSize.getWidth();
    const baseTable = {
      styles: {
        font: 'helvetica',
        fontSize: 9,
        cellPadding: 5,
        overflow: 'linebreak',
        textColor: [30, 30, 30],
      },
      headStyles: {
        fillColor: [20, 55, 90],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 9,
      },
      margin: { left: marginX, right: marginX },
      tableWidth: pageWidth - marginX * 2,
      theme: 'striped',
      alternateRowStyles: { fillColor: [248, 250, 252] },
    };

    let y = 38;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text('Partner Report', pageWidth / 2, y, { align: 'center' });
    y += 22;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`Generated: ${dayjs().format('YYYY-MM-DD HH:mm')}`, marginX, y);
    y += 14;
    doc.text(`Generated by: ${currentUser?.name || 'System'}`, marginX, y);
    y += 14;
    doc.text(`Status filter: ${filters.status || 'All'}`, marginX, y);
    y += 14;
    doc.text(`Search: ${searchTerm || 'None'}`, marginX, y);
    y += 14;
    doc.text(`Records: ${rows.length}`, marginX, y);
    y += 18;

    autoTable(doc, {
      ...baseTable,
      startY: y,
      head: [[
        'No',
        'Company',
        'Tier',
        'Amount (RM)',
        'Status',
        'Publicity',
        'Start',
        'End',
        'Notes',
      ]],
      body: rows.map((row) => [
        row.no,
        row.company,
        row.tier,
        row.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        row.status,
        row.publicity,
        row.startDate,
        row.endDate,
        row.notes,
      ]),
      columnStyles: {
        0: { cellWidth: 24, halign: 'center' },
        1: { cellWidth: 90 },
        2: { cellWidth: 64 },
        3: { cellWidth: 60, halign: 'right' },
        4: { cellWidth: 52, halign: 'center' },
        5: { cellWidth: 58 },
        6: { cellWidth: 56 },
        7: { cellWidth: 56 },
        8: { cellWidth: 'auto' },
      },
      didDrawPage: () => {
        const pageCount = doc.getNumberOfPages();
        const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(120, 120, 120);
        doc.text(
          `Page ${currentPage} of ${pageCount}`,
          pageWidth - marginX,
          doc.internal.pageSize.getHeight() - 16,
          { align: 'right' }
        );
      },
    });

    doc.save(`Partner_Report_${dayjs().format('YYYYMMDD_HHmmss')}.pdf`);
  };

  const handleGeneratePartnerReport = async (type) => {
    try {
      setReportLoading(true);
      closeReportMenu();
      if (type === 'csv') {
        generatePartnerCsvReport();
      } else {
        generatePartnerPdfReport();
      }
      setSuccess(`Partner report (${type.toUpperCase()}) generated successfully.`);
    } catch { setError('Failed to generate report. Please try again.'); }
    finally { setReportLoading(false); }
  };

  const activeStatFilter = activeStatFilterFromFilters(filters);

  const handleFilterChange = (newFilters) => {
    const archivedViewChanged = (filters.status === 'archived') !== (newFilters.status === 'archived');
    setFilters(newFilters);
    setPage(0);
    if (archivedViewChanged) {
      clearSelection();
      fetchPartners({ forceRefresh: true, archived: newFilters.status === 'archived' });
    }
  };

  const handleClearFilters = () => {
    const wasArchived = filters.status === 'archived';
    setFilters({});
    setSearchTerm('');
    setPage(0);
    if (wasArchived) fetchPartners({ forceRefresh: true });
  };

  const handleStatCardClick = (_, index) => {
    const filterMap = ['all', 'active', 'year_all', 'premium'];
    const selectedFilter = filterMap[index] || 'all';
    const nextFilter = activeStatFilter === selectedFilter ? 'all' : selectedFilter;

    const nextFilters = {};
    if (nextFilter === 'active') {
      nextFilters.status = 'active';
      nextFilters.current_year = '1';
    } else if (nextFilter === 'year_all') {
      nextFilters.current_year = '1';
    } else if (nextFilter === 'premium') {
      nextFilters.premium_tier = '1';
      nextFilters.current_year = '1';
    }

    setFilters(nextFilters);
    setPage(0);
  };

  const activeStatIndex = STAT_FILTER_TO_INDEX[activeStatFilter] ?? 0;

  const filteredPartners = partners.filter(partner => {
    const q = searchTerm.toLowerCase();
    const repName = partner.company?.representative?.name?.toLowerCase() ?? '';
    const matchesSearch =
      partner.company?.name.toLowerCase().includes(q) ||
      partner.notes?.toLowerCase().includes(q) ||
      repName.includes(q);
    const matchesFilters = Object.entries(filters).every(([key, value]) => {
      if (!value) return true;
      if (key === 'status') return partnerDisplayStatus(partner) === value;
      if (key === 'company_id') return String(partner.company_id ?? partner.company?.id ?? '') === String(value);
      if (key === 'representative_user_id') {
        return String(getPartnerRepresentativeId(partner) ?? '') === String(value);
      }
      if (key === 'sponsor_start_date') {
        if (!partner.sponsor_start_date) return false;
        return dayjs(partner.sponsor_start_date).format('YYYY-MM-DD') >= String(value);
      }
      if (key === 'sponsor_end_date') {
        if (!partner.sponsor_end_date) return false;
        return dayjs(partner.sponsor_end_date).format('YYYY-MM-DD') <= String(value);
      }
      if (key === 'premium_tier') return isPremiumSlug(tierSlug(partner));
      if (key === 'current_year') return value === '1' ? partnerInCurrentYear(partner) : true;
      return true;
    });
    return matchesSearch && matchesFilters;
  });

  const sortedPartners = [...filteredPartners].sort((a, b) => {
    const tierA = TIER_SORT_ORDER[tierSlug(a)] ?? 99;
    const tierB = TIER_SORT_ORDER[tierSlug(b)] ?? 99;
    if (tierA !== tierB) return tierA - tierB;

    const statusA = STATUS_SORT_ORDER[a.status] ?? 98;
    const statusB = STATUS_SORT_ORDER[b.status] ?? 98;
    if (statusA !== statusB) return statusA - statusB;

    return (a.company?.name || '').localeCompare(b.company?.name || '');
  });
  const hasActiveCompanies = companies.some((company) => company.status !== 'inactive');

  // ─── STATS CALCULATED FROM ALL DATA (NOT FILTERED) ───────────────────────────────
  const yearPartners = partners.filter(partnerInCurrentYear);
  const totalYearValue = yearPartners.reduce((sum, p) => sum + (parseFloat(p.sponsor_amount) || 0), 0);

  const statsData = [
    { icon: PeopleIcon, value: yearPartners.length, label: `Partners (${CURRENT_YEAR})`, color: 'info' },
    { icon: ActiveIcon, value: yearPartners.filter((p) => p.status === 'active').length, label: `Active (${CURRENT_YEAR})`, color: 'success' },
    { icon: MoneyIcon, value: `RM ${totalYearValue.toLocaleString()}`, label: `Value (${CURRENT_YEAR})`, color: 'warning', valueSize: 'small' },
    { icon: StarIcon, value: yearPartners.filter((p) => isPremiumSlug(tierSlug(p))).length, label: `Premium (${CURRENT_YEAR})`, color: 'secondary' },
  ];

  const columns = [
    {
      key: 'company_name', label: 'Company', icon: BusinessIcon, minWidth: 250,
      sortAccessor: (p) => p.company?.name || '',
      render: (partner) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 40, height: 40, background: `linear-gradient(135deg, ${alpha(NEON, 0.3)}, ${alpha(B[700], 0.6)})`, border: `2px solid ${alpha(NEON, 0.3)}`, color: '#fff' }}>
            <BusinessIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Box>
            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.875rem', color: 'rgba(226,232,240,0.9)' }}>{partner.company?.name || '—'}</Typography>
            <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', color: alpha(NEON, 0.45) }}>ID: {partner.id}</Typography>
          </Box>
        </Box>
      ),
    },
    {
      key: 'tier', label: 'Tier', icon: StarIcon, minWidth: 160,
      sortAccessor: (p) => p.sponsor_tier?.tier_name || '',
      render: (partner) => {
        const slug = tierSlug(partner);
        const n = getLevelNeon(slug);
        const name = partner.sponsor_tier?.tier_name || '—';
        const label = slug ? `${getLevelEmoji(slug)} ${name}` : name;
        return (
          <Chip label={label} size="small"
            sx={{ height: 22, fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', fontWeight: 600, bgcolor: alpha(n, 0.1), color: n, border: `1px solid ${alpha(n, 0.35)}`, boxShadow: `0 0 8px ${alpha(n, 0.15)}`, '& .MuiChip-label': { px: 1 } }}
          />
        );
      },
    },
    {
      key: 'sponsor_amount', label: 'Sponsor amount', icon: MoneyIcon, minWidth: 140,
      sortAccessor: (p) => parseFloat(p.sponsor_amount || 0),
      render: (partner) => (
        <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.78rem', fontWeight: 600, color: NEON3 }}>
          RM {parseFloat(partner.sponsor_amount || 0).toLocaleString()}
        </Typography>
      ),
    },
    {
      key: 'status', label: 'Status', minWidth: 120,
      render: (partner) => {
        const displayStatus = partnerDisplayStatus(partner);
        const n = getStatusNeon(displayStatus);
        const label = getStatusLabel(displayStatus);
        return (
          <Chip label={label} size="small"
            sx={{ height: 22, fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', fontWeight: 600, bgcolor: alpha(n, 0.1), color: n, border: `1px solid ${alpha(n, 0.35)}`, boxShadow: `0 0 8px ${alpha(n, 0.15)}`, '& .MuiChip-label': { px: 1 } }}
          />
        );
      },
    },
    {
      key: 'notes', label: 'Notes', icon: AssignmentIcon, minWidth: 200,
      render: (partner) => (
        <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: 'rgba(148,163,184,0.85)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
          {partner.notes || '—'}
        </Typography>
      ),
    },
    {
      key: 'actions', label: 'Actions', sortable: false, align: 'right', minWidth: 130,
      render: (partner) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Tooltip title="View Partner" arrow>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/partners/${partner.id}`); }}
              sx={{
                color: theme.palette.info.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                }
              }}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canManagePartnerships && (
            <>
              {!partner.archived_at && <Tooltip title="Edit Partner" arrow>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/partners/edit/${partner.id}`); }}
                  sx={{
                    color: theme.palette.warning.main,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.info.main, 0.1),
                    }
                  }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>}
              {partner.archived_at && (
                <Tooltip title="Restore Partner" arrow>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRestore(partner); }}
                    sx={{ color: theme.palette.success.main, '&:hover': { backgroundColor: alpha(theme.palette.success.main, 0.1) } }}>
                    <RestoreIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {!partner.archived_at && (
                <Tooltip title="Archive Partner" arrow>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(partner); }}
                    sx={{
                      color: theme.palette.error.main,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                      }
                    }}>
                    <ArchiveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </Box>
      ),
    },
  ];

  if (!currentUser || !hasPartnershipAccess) return null;
  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
      <Loading message="Loading partners list" size={56} variant="primary" />
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>
      <Header
        title="Partner Management"
        subtitle="Manage partnerships and sponsorship relationships"
        icon={HandshakeIcon}
        actionButton={
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={reportLoading ? <CircularProgress size={14} color="inherit" /> : <ReportIcon />}
              onClick={openReportMenu}
              disabled={reportLoading || sortedPartners.length === 0}
              sx={{ height: 'auto', borderRadius: '10px', px: 2.5, py: 1, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.875rem', bgcolor: alpha('#fff', 0.1), color: '#fff', borderColor: alpha('#fff', 0.3), backdropFilter: 'blur(8px)', '&:hover': { bgcolor: alpha('#fff', 0.2), borderColor: alpha('#fff', 0.5) }, '&.Mui-disabled': { bgcolor: alpha('#fff', 0.05), color: alpha('#fff', 0.3) } }}>
              {reportLoading ? 'Generating…' : 'Generate Report'}
            </Button>
            {canManagePartnerships && (
              <Tooltip
                title={hasActiveCompanies ? '' : 'Create or reactivate a company before adding a partner.'}
                arrow
              >
                <span>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/partners/add')}
                    disabled={!hasActiveCompanies}
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.22)',
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.34)',
                      },
                      borderRadius: 2,
                      px: 2.5,
                      py: 1.5,
                      minWidth: { xs: '100%', sm: 170 },
                      flex: { xs: '1 1 100%', sm: '0 1 auto' }
                    }}
                  >
                    Add Partner
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        }
      />

      <Menu
        anchorEl={reportMenuAnchor}
        open={isReportMenuOpen}
        onClose={closeReportMenu}
        PaperProps={{
          elevation: 6,
          sx: {
            mt: 1,
            minWidth: 210,
            borderRadius: 2,
            border: `1px solid ${alpha('#38BDF8', 0.28)}`,
            background: `linear-gradient(155deg, ${alpha('#050d1a', 0.98)}, ${alpha('#0a1628', 0.95)})`,
            backdropFilter: 'blur(8px)',
            boxShadow: `0 8px 32px ${alpha('#000', 0.55)}`,
            '& .MuiMenuItem-root': {
              color: alpha('#E2E8F0', 0.9),
              borderRadius: 1.25,
              mx: 0.5,
              my: 0.25,
              '& .MuiListItemIcon-root': {
                color: alpha('#38BDF8', 0.88),
                minWidth: 32,
              },
              '& .MuiListItemText-secondary': {
                color: alpha('#94A3B8', 0.88),
              },
              '&:hover': {
                backgroundColor: alpha('#38BDF8', 0.12),
              },
            },
          },
        }}
      >
        <MenuItem onClick={() => handleGeneratePartnerReport('csv')} disabled={reportLoading}>
          <ListItemIcon>
            <CsvIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Download CSV Report"
            secondary="Spreadsheet-compatible format"
          />
        </MenuItem>
        <MenuItem onClick={() => handleGeneratePartnerReport('pdf')} disabled={reportLoading}>
          <ListItemIcon>
            <PdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Download PDF Report"
            secondary="Printable report format"
          />
        </MenuItem>
      </Menu>

      <FeedbackMessage
        message={error}
        severity="error"
        onClose={() => setError(null)}
        sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
          background: alpha('#450a0a', 0.92),
          border: '1px solid rgba(248,113,113,0.3)',
          boxShadow: '0 4px 20px rgba(239,68,68,0.12)',
          color: '#fca5a5',
          mb: 3,
        }}
      />

      <FeedbackMessage
        message={success}
        severity="success"
        onClose={() => setSuccess(null)}
        sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
          background: alpha(SUCCESS, 0.12),
          border: `1px solid ${alpha(SUCCESS, 0.25)}`,
          color: alpha(SUCCESS, 0.95),
          mb: 3,
        }}
      />

      <StatCards stats={statsData} onCardClick={handleStatCardClick} activeIndex={activeStatIndex} />

      {/* Dark glass filter card */}
      <MotionCard
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.16 }}
        elevation={0}
        sx={{ ...cardShellSx, mb: 2.5 }}
      >
        <Box sx={{ position: 'absolute', inset: 0, background: DOT_GRID, pointerEvents: 'none', zIndex: 0 }} />
        <CardContent sx={{ p: 2.5, position: 'relative', zIndex: 1 }}>
          <FilterBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            showFilters={showFilters}
            onToggleFilters={() => setShowFilters(!showFilters)}
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            filterOptions={filterOptions}
            searchPlaceholder="Search by company name, assignee, or notes..."
          />
        </CardContent>
      </MotionCard>
      {filters.status !== 'archived' && <ListBulkToolbar
        config={PARTNER_BULK_CONFIG}
        currentUser={currentUser}
        selectedIds={selectedIds}
        onClearSelection={clearSelection}
        {...createBulkHandlers({
          endpoint: PARTNER_BULK_CONFIG.endpoint,
          setItems: setPartners,
          selectedIds,
          clearSelection,
          setSuccess,
          successLabel: 'partner(s)',
          refetch: fetchPartners,
          setError
        })}
        onDeleteSuccess={handleBulkArchive}
      />}



      <TableList
        columns={columns}
        data={sortedPartners}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        {...(filters.status !== 'archived' ? bulkTableProps(PARTNER_BULK_CONFIG, currentUser, selectedIds, setSelectedIds) : {})}
        loading={loading}
        emptyState={{ icon: <HandshakeIcon />, title: 'No partners found', description: searchTerm || Object.keys(filters).length > 0 ? 'Try adjusting your search or filters.' : 'Start by adding your first partnership.' }}
        onRowClick={(row) => navigate(`/partners/${row.id}`)}
      />

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setSelectedPartner(null); }}
        onConfirm={confirmDelete}
        loading={deleteLoading}
        itemType="partner"
        itemName={selectedPartner?.company?.name}
        title="Archive Partner"
        description="The partner will be archived (soft-deleted), hidden from public display, and retained in the database for recovery."
        confirmLabel="Archive Partner"
        actionVerb="archive"
      />
    </Container>
  );
};

export default PartnerList;
